import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Menu, X } from 'lucide-react';

export const Navigation = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { name: 'what we do', path: '#services' },
    { name: 'how we work', path: '#howwework' },
    { name: 'selected work', path: '#work' },
    { name: 'contact', path: '#contact' },
    { name: 'insights', path: '/insights' }
  ];

  const handleNavClick = (e, path) => {
    e.preventDefault();
    if (path.startsWith('#')) {
      if (location.pathname !== '/') {
        navigate('/' + path);
      } else {
        const targetId = path.substring(1);
        const element = document.getElementById(targetId);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }
    } else {
      navigate(path);
    }
    setIsMobileMenuOpen(false);
  };

  return (
    <>
      {/* Floating Pill-shaped Navbar with Liquid Glass Effect */}
      <nav className="fixed top-4 left-1/2 transform -translate-x-1/2 z-50 transition-all duration-500 w-[92%] md:w-[80%]">
        <div className={`backdrop-blur-lg bg-white/50 rounded-full shadow-lg border border-white/20 px-4 md:px-8 py-2 h-12 transition-all duration-300 ${
          isScrolled ? 'shadow-xl backdrop-blur-xl bg-white/20' : 'shadow-lg backdrop-blur-lg bg-white/50'
        }`}>
          <div className="flex items-center justify-between w-full h-full">
            {/* Logo - Left */}
            <a 
              href="/" 
              className="flex items-center hover:opacity-80 transition-opacity duration-300 md:-ml-6"
              style={{ marginLeft: '-0.5rem' }}
            >
              <span 
                className="rounded-full items-center justify-center text-white font-bold tracking-tight leading-none hidden md:inline-flex"
                style={{ backgroundImage: `url('${process.env.REACT_APP_BACKGROUND_IMAGE_URL}')`, backgroundSize: 'cover', backgroundPosition: 'center', fontSize: '2.25rem', height: '2.5rem', paddingLeft: '0.75rem', paddingRight: '0.75rem', lineHeight: '1', paddingBottom: '0.1rem' }}
              >
                anyframe
              </span>
              <span 
                className="rounded-full items-center justify-center text-white font-bold tracking-tight leading-none inline-flex md:hidden"
                style={{ backgroundImage: `url('${process.env.REACT_APP_BACKGROUND_IMAGE_URL}')`, backgroundSize: 'cover', backgroundPosition: 'center', fontSize: '1.25rem', height: '1.75rem', paddingLeft: '0.5rem', paddingRight: '0.5rem', lineHeight: '1' }}
              >
                anyframe
              </span>
            </a>

            {/* Desktop Navigation Links - Right */}
            <div className="hidden md:flex items-center space-x-4 lg:space-x-8">
              {navLinks.map((link) => (
                <a
                  key={link.name}
                  href={link.path}
                  onClick={(e) => handleNavClick(e, link.path)}
                  className={`text-sm font-bold tracking-wide transition-all duration-300 hover:text-black hover:-translate-y-0.5 px-4 py-2 rounded-full hover:bg-white hover:shadow-md cursor-pointer`}
                >
                  {link.name}
                </a>
              ))}
            </div>

            {/* Mobile Menu Button - Right (only visible on mobile) */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="md:hidden p-3 text-black/80 hover:text-black transition-all duration-200 rounded-full hover:bg-white/30 backdrop-blur-sm"
              aria-label="Menu"
            >
              {isMobileMenuOpen ? <X size={20} /> : <Menu size={20} />}
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile Navigation Dropdown */}
      <div className={`md:hidden fixed top-16 left-1/2 transform -translate-x-1/2 z-40 transition-all duration-300 ease-in-out w-[92%] ${
        isMobileMenuOpen 
          ? 'opacity-100 translate-y-0 pointer-events-auto' 
          : 'opacity-0 -translate-y-4 pointer-events-none'
      }`}>
        <div className="backdrop-blur-xl bg-white/80 rounded-2xl shadow-xl border border-white/30 px-6 py-4">
          <div className="space-y-2">
            {navLinks.map((link) => (
              <a
                key={link.name}
                href={link.path}
                onClick={(e) => handleNavClick(e, link.path)}
                className="block rounded-full px-4 py-2 bg-black text-white text-sm font-bold tracking-wide text-center transition-all duration-300 hover:shadow-md cursor-pointer"
              >
                {link.name}
              </a>
            ))}
          </div>
        </div>
      </div>

      {/* Backdrop for mobile menu */}
      {isMobileMenuOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-20 z-30 transition-opacity duration-300"
          onClick={() => setIsMobileMenuOpen(false)}
        />
      )}
    </>
  );
};